import React from "react";
/* Button — Phase B primitive (refreshed in Phase 2 Atoms).
   Variants: primary / secondary / outline / ghost / danger.
   Sizes:    sm (28h) / md (32h, default) / lg (36h).
   Tones:    warn / success / info / danger / accent — optional accent
             overlay (color, not background) on top of any variant.

   variant vs tone — important distinction:
     variant   structural treatment of the entire button (filled / bordered /
               transparent). Drives the background + main color.
     tone      one-color text-tint overlay. Layered onto a ghost button to
               make the affordance read as "danger action" without becoming
               a full danger button. Don't combine tone=danger with
               variant=danger (the variant already carries the meaning). */

export type ButtonVariant = "primary" | "secondary" | "outline" | "ghost" | "danger";
export type ButtonSize = "sm" | "md" | "lg";
export type ButtonTone = "warn" | "success" | "info" | "danger" | "accent";
/** Force a visual state for Lab matrix views. Skip in production. */
export type ButtonForcedState = "hover" | "focus" | "active";

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  iconOnly?: boolean;
  loading?: boolean;
  active?: boolean;
  tone?: ButtonTone;
  /** Lab-only: pin the visual state without user interaction. */
  forcedState?: ButtonForcedState;
}

export default function Button({
  size = "md",
  variant = "primary",
  iconOnly = false,
  loading = false,
  disabled = false,
  active = false,
  tone,
  forcedState,
  type = "button",
  className = "",
  children,
  ...rest
}: ButtonProps) {
  const cls = [
    "v2-btn",
    `v2-btn--${variant}`,
    `v2-btn--${size}`,
    iconOnly ? "v2-btn--icon" : "",
    active ? "v2-btn--active" : "",
    tone ? `v2-btn--tone-${tone}` : "",
    loading ? "v2-btn--loading" : "",
    forcedState ? `v2-btn--state-${forcedState}` : "",
    className,
  ].filter(Boolean).join(" ");
  return (
    <button
      type={type}
      className={cls}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      aria-pressed={(active && variant === "ghost") ? true : undefined}
      {...rest}
    >
      {loading ? <span className="v2-btn__spinner" aria-hidden="true" /> : null}
      <span className="v2-btn__content">{children}</span>
    </button>
  );
}
